import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useApiFetcher } from "../ApiProvider";
import { QUERY_KEYS } from "../queryKeys";
import type { UseQueryOptions } from "@tanstack/react-query";

// ---------------------------------------------------------------------------
// Shared option types
// ---------------------------------------------------------------------------

/**
 * Partial query options callers can pass to override defaults.
 * `queryKey` and `queryFn` are always owned by the hook.
 */
type OverrideQueryOptions<T> = Omit<Partial<UseQueryOptions<T>>, "queryKey" | "queryFn">;
export interface ProductivityChartQueryOptions {
  /** Skips expensive distraction URL name lookups on the server (completion chart). */
  skipUrlLookups?: boolean;
}

// ---------------------------------------------------------------------------
// Leaderboard period type (shared across consumers)
// ---------------------------------------------------------------------------

export type LeaderboardPeriod = "daily" | "weekly" | "monthly" | "all";

// ---------------------------------------------------------------------------
// useProductivityChart
// ---------------------------------------------------------------------------

/**
 * Fetches productivity buckets for a given session.
 *
 * @param sessionId - The session to fetch data for (null/undefined disables the query)
 * @param options - Override any useQuery option (staleTime, gcTime, refetchInterval, etc.)
 */
export function useProductivityChart(sessionId, options) {
  const $ = _c(19);
  const apiFetcher = useApiFetcher();
  let t0;
  if ($[0] !== sessionId) {
    t0 = !sessionId || sessionId.startsWith("temp-") || sessionId === "temp-id";
    $[0] = sessionId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isTemporary = t0;
  let t1;
  if ($[2] !== options) {
    t1 = options ?? {};
    $[2] = options;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let queryOptions;
  let skipUrlLookups;
  if ($[4] !== t1) {
    ({
      skipUrlLookups,
      ...queryOptions
    } = t1);
    $[4] = t1;
    $[5] = queryOptions;
    $[6] = skipUrlLookups;
  } else {
    queryOptions = $[5];
    skipUrlLookups = $[6];
  }
  let t2;
  if ($[7] !== sessionId) {
    t2 = QUERY_KEYS.productivityData(sessionId ?? "");
    $[7] = sessionId;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== apiFetcher || $[10] !== isTemporary || $[11] !== sessionId || $[12] !== skipUrlLookups) {
    t3 = async () => {
      if (isTemporary) {
        return [] as unknown as TData;
      }
      const suffix = skipUrlLookups ? "?skipUrlLookups=true" : "";
      return apiFetcher(`/api/v1/sessions/productivity/${sessionId}${suffix}`);
    };
    $[9] = apiFetcher;
    $[10] = isTemporary;
    $[11] = sessionId;
    $[12] = skipUrlLookups;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const t4 = !isTemporary;
  let t5;
  if ($[14] !== queryOptions || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      staleTime: 60000,
      gcTime: 3600000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      placeholderData: _temp,
      ...queryOptions
    };
    $[14] = queryOptions;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return useQuery(t5);
}

// ---------------------------------------------------------------------------
// useProductivityLeaderboard
// ---------------------------------------------------------------------------

/**
 * Fetches the productivity leaderboard.
 *
 * @param limit - Max entries to return (default 10)
 * @param period - Time period filter (default "weekly")
 * @param options - Override any useQuery option
 */
function _temp(prev) {
  return prev;
}
export function useProductivityLeaderboard(t0, t1, options) {
  const $ = _c(11);
  const limit = t0 === undefined ? 10 : t0;
  const period = t1 === undefined ? "weekly" : t1;
  const apiFetcher = useApiFetcher();
  let t2;
  if ($[0] !== limit || $[1] !== period) {
    t2 = QUERY_KEYS.productivityLeaderboard(limit, period);
    $[0] = limit;
    $[1] = period;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== apiFetcher || $[4] !== limit || $[5] !== period) {
    t3 = () => apiFetcher(`/api/v1/sessions/productivity/leaderboard?limit=${limit}&period=${period}`);
    $[3] = apiFetcher;
    $[4] = limit;
    $[5] = period;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== options || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      staleTime: 300000,
      gcTime: 300000,
      ...options
    };
    $[7] = options;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return useQuery(t4);
}

// ---------------------------------------------------------------------------
// useRecentSessions
// ---------------------------------------------------------------------------

/**
 * Fetches recent productivity sessions.
 *
 * @param limit - Max sessions to return (default 5)
 * @param options - Override any useQuery option
 */
export function useRecentSessions(t0, options) {
  const $ = _c(9);
  const limit = t0 === undefined ? 5 : t0;
  const apiFetcher = useApiFetcher();
  let t1;
  if ($[0] !== limit) {
    t1 = QUERY_KEYS.recentSessions(limit);
    $[0] = limit;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== apiFetcher || $[3] !== limit) {
    t2 = () => apiFetcher(`/api/v1/sessions/productivity/recent?limit=${limit}`);
    $[2] = apiFetcher;
    $[3] = limit;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== options || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      staleTime: 300000,
      gcTime: 300000,
      retry: 2,
      retryDelay: _temp2,
      placeholderData: _temp3,
      ...options
    };
    $[5] = options;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
}

// ---------------------------------------------------------------------------
// usePaginatedRecentSessions
// ---------------------------------------------------------------------------

/**
 * Fetches paginated recent productivity sessions.
 *
 * @param page - Page number (default 1)
 * @param limit - Items per page (default 20)
 * @param options - Override any useQuery option
 */
function _temp3(previousData) {
  return previousData;
}
function _temp2(attemptIndex) {
  return Math.min(1000 * 2 ** attemptIndex, 8000);
}
export function usePaginatedRecentSessions(t0, t1, options) {
  const $ = _c(11);
  const page = t0 === undefined ? 1 : t0;
  const limit = t1 === undefined ? 20 : t1;
  const apiFetcher = useApiFetcher();
  let t2;
  if ($[0] !== limit || $[1] !== page) {
    t2 = QUERY_KEYS.paginatedRecentSessions(page, limit);
    $[0] = limit;
    $[1] = page;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== apiFetcher || $[4] !== limit || $[5] !== page) {
    t3 = () => apiFetcher(`/api/v1/sessions/productivity/recent/paginated?page=${page}&limit=${limit}`);
    $[3] = apiFetcher;
    $[4] = limit;
    $[5] = page;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== options || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      staleTime: 300000,
      gcTime: 300000,
      retry: 2,
      retryDelay: _temp4,
      placeholderData: _temp5,
      ...options
    };
    $[7] = options;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return useQuery(t4);
}

// ---------------------------------------------------------------------------
// useUserStats
// ---------------------------------------------------------------------------

/**
 * Fetches user stats (completed today, total focus minutes, etc.).
 *
 * @param options - Override any useQuery option
 */
function _temp5(previousData) {
  return previousData;
}
function _temp4(attemptIndex) {
  return Math.min(1000 * 2 ** attemptIndex, 8000);
}
export function useUserStats(options) {
  const $ = _c(5);
  const apiFetcher = useApiFetcher();
  let t0;
  if ($[0] !== apiFetcher) {
    t0 = () => apiFetcher("/api/v1/user/stats");
    $[0] = apiFetcher;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      queryKey: QUERY_KEYS.USER_STATS,
      queryFn: t0,
      staleTime: 60000,
      gcTime: 300000,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useQuery(t1);
}

// ---------------------------------------------------------------------------
// useStreak
// ---------------------------------------------------------------------------

export interface StreakData {
  currentStreak: number;
  longestStreak: number;
}

/**
 * Fetches both current and longest streak for a user.
 * Combines the old useCurrentStreak + useLongestStreak into one call.
 *
 * @param userId - User to fetch streaks for (undefined disables the query)
 * @param options - Override any useQuery option
 */
export function useStreak(userId, options) {
  const $ = _c(10);
  const apiFetcher = useApiFetcher();
  let t0;
  if ($[0] !== userId) {
    t0 = QUERY_KEYS.streak(userId ?? "");
    $[0] = userId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== apiFetcher || $[3] !== userId) {
    t1 = async () => {
      if (!userId) {
        return {
          currentStreak: 0,
          longestStreak: 0
        };
      }
      const data = await apiFetcher(`/api/v1/user/${userId}/streak`);
      return {
        currentStreak: data.currentStreak ?? 0,
        longestStreak: data.longestStreak ?? 0
      };
    };
    $[2] = apiFetcher;
    $[3] = userId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = !!userId;
  let t3;
  if ($[5] !== options || $[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 300000,
      gcTime: 600000,
      ...options
    };
    $[5] = options;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useQuery(t3);
}

// ---------------------------------------------------------------------------
// useActivityStreak
// ---------------------------------------------------------------------------

/**
 * Fetches daily activity data for a date range (focus minutes, session counts).
 *
 * @param startDate - ISO date string for the range start
 * @param days - Number of days to fetch
 * @param options - Override any useQuery option
 */
export function useActivityStreak(startDate, days, options) {
  const $ = _c(11);
  const apiFetcher = useApiFetcher();
  let t0;
  if ($[0] !== days || $[1] !== startDate) {
    t0 = QUERY_KEYS.activityStreak(startDate, days);
    $[0] = days;
    $[1] = startDate;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== apiFetcher || $[4] !== days || $[5] !== startDate) {
    t1 = () => apiFetcher(`/api/v1/user/activity?startDate=${startDate}&days=${days}`);
    $[3] = apiFetcher;
    $[4] = days;
    $[5] = startDate;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== options || $[8] !== t0 || $[9] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      staleTime: 300000,
      gcTime: 600000,
      ...options
    };
    $[7] = options;
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return useQuery(t2);
}

// ---------------------------------------------------------------------------
// useSessionHistory
// ---------------------------------------------------------------------------

/**
 * Fetches session history for the heatmap / activity overview.
 *
 * @param days - Number of days of history to fetch
 * @param options - Override any useQuery option
 */
export function useSessionHistory(days, options) {
  const $ = _c(9);
  const apiFetcher = useApiFetcher();
  let t0;
  if ($[0] !== days) {
    t0 = QUERY_KEYS.sessionHistory(days);
    $[0] = days;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== apiFetcher || $[3] !== days) {
    t1 = () => apiFetcher(`/api/v1/user/activity/history?days=${days}`);
    $[2] = apiFetcher;
    $[3] = days;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== options || $[6] !== t0 || $[7] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      staleTime: 300000,
      gcTime: 600000,
      ...options
    };
    $[5] = options;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return useQuery(t2);
}